/**
 * The browser's direct client for ProductData customer routes (goal 248).
 *
 * Replaces the Vercel proxy hop: the browser mints the Better Auth JWT itself
 * (`getWorkerBearerToken`) and calls the Worker's origin directly. The Worker
 * re-verifies that JWT on every customer route, so the proxy was adding a hop,
 * not a check.
 *
 * ENVELOPE UNWRAP IS NARROW ON PURPOSE (goal 249 follow-up, controller ruling
 * R20). A parsed 2xx/4xx JSON body is treated as an envelope in exactly two
 * cases:
 *   - `{ success: false, error: { code, message } }` — throws a
 *     `ProductDataError` carrying that code/message (falling back to
 *     `HTTP_ERROR` / `HTTP <status>` when either is missing).
 *   - `{ success: false, error: '<string>' }` — some Worker routes (e.g.
 *     state-tax self-filed create/delete) answer a plain string instead of
 *     `{code,message}` (controller ruling R34). Throws
 *     `ProductDataError(status, 'HTTP_ERROR', error)`, preserving that exact
 *     message instead of collapsing it to a generic `HTTP <status>`.
 *   - `{ success: true, data: <T> }` — returns `data` unwrapped, INCLUDING
 *     when `data` is explicitly `null` (the key's presence is what matters,
 *     not its value).
 * Anything else passes through as `T` completely untouched — most sharply, a
 * body that carries `success: true` but no `data` key at all, such as
 * `{ success: true, address: {...} }` (companies address create) or a bare
 * `{ success: true }` (companies address/shareholder update). Those are NOT
 * envelopes; they are the Worker's own Vercel-parity response shape, and
 * unwrapping them by the mere presence of a truthy `success` field (the prior
 * behavior) silently discarded everything but a nonexistent `data` key. Some
 * routes also answer fully raw with no `success` field at all
 * (`/api/customer/dashboard`, `/api/customer/services/active`) — those hit
 * this same pass-through. Reproducing all of this exactly is what keeps each
 * hook's body unchanged when it switches transport.
 *
 * `credentials: 'omit'` is not incidental. The Worker's CORS is
 * `credentials: false`, so sending cookies would make the browser reject the
 * response; omitting them also guarantees the session cookie never crosses an
 * origin that has no use for it.
 */
import type { RuleViolation } from '@nt/onboarding-rules'

import { getWorkerBearerToken } from '@/lib/auth/client'

/**
 * The ProductData response contract every `productData()` caller reasons about,
 * and the shape the unwrap rule documented above is written against.
 *
 * @public
 */
export type Envelope<T> = { success: true; data: T } | { success: false; error: { code: string; message: string } }

export class ProductDataError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    options?: { cause?: unknown; violations?: RuleViolation[]; orderId?: string },
  ) {
    super(message, options)
    this.name = 'ProductDataError'
    this.violations = options?.violations
    this.orderId = options?.orderId
  }

  /**
   * The order a 409 points at (state filings: `ORDER_EXISTS`, `YEAR_CLOSED`), so the page can open
   * it. `undefined` for every other error.
   */
  readonly orderId?: string

  /**
   * Structured `{ code, field, message }` rule failures, additive on an
   * `INVALID_DRAFT_DATA` onboarding-completion refusal (owner decision ob-03
   * (2)). `undefined` for every other error — most routes never send this.
   */
  readonly violations?: RuleViolation[]
}

export function productDataBaseUrl(): string {
  const base = process.env.VITE_PRODUCT_DATA_URL
  if (!base) {
    throw new ProductDataError(0, 'NOT_CONFIGURED', 'VITE_PRODUCT_DATA_URL is not set')
  }
  return base.replace(/\/$/, '')
}

async function authorizedInit(init: RequestInit, options: { freshToken?: boolean } = {}): Promise<RequestInit> {
  let token: string | null
  try {
    token = await getWorkerBearerToken(options.freshToken ? { fresh: true } : undefined)
  } catch (cause) {
    // The token call got no answer (dropped, aborted, auth Worker 5xx). That
    // is a transport failure like `send`'s below — callers sign out on a 401,
    // so it must never be reported as one.
    throw new ProductDataError(0, 'NETWORK', 'Session token request failed to send', { cause })
  }
  if (!token) {
    throw new ProductDataError(401, 'UNAUTHENTICATED', 'No session token')
  }
  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${token}`)
  // FormData must keep the browser's own multipart boundary; setting a
  // content-type by hand would corrupt the upload.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (init.body && !isFormData && !headers.has('content-type')) {
    headers.set('content-type', 'application/json')
  }
  return { ...init, headers, credentials: 'omit' }
}

async function send(path: string, init: RequestInit): Promise<Response> {
  const url = `${productDataBaseUrl()}${path}`
  try {
    return await fetch(url, init)
  } catch (cause) {
    // `fetch` rejects with a bare TypeError when the request never reached the
    // Worker (offline, DNS, blocked). Classify it so callers can branch.
    throw new ProductDataError(0, 'NETWORK', 'ProductData request failed to send', { cause })
  }
}

let writesSent = 0

/**
 * How many writes `productData` has sent since the page loaded. A cached read
 * remembers the count it was taken at and is stale once the count moves: any
 * write may have changed the thing it read.
 */
export function productDataWriteCount(): number {
  return writesSent
}

/** How many times a rate-limited read is repeated before the 429 is reported. */
const RATE_LIMIT_RETRIES = 3
const RATE_LIMIT_DEFAULT_WAIT_MS = 1_000
/** One wait never exceeds the Worker's own 10s customer window. */
const RATE_LIMIT_MAX_WAIT_MS = 10_000

function rateLimitWaitMs(response: Response): number {
  const seconds = Number(response.headers.get('Retry-After'))
  if (!Number.isFinite(seconds) || seconds <= 0) return RATE_LIMIT_DEFAULT_WAIT_MS
  return Math.min(Math.max(seconds * 1000, RATE_LIMIT_DEFAULT_WAIT_MS), RATE_LIMIT_MAX_WAIT_MS)
}

/**
 * The customer surface has a read budget per customer (120 GETs per 10
 * seconds; writes have their own, 60 per 10 seconds,
 * `apps/api/src/rate-limit/customer-tiers.ts`), so a read can land just
 * over it. That is a wait, not a failure: the Worker answers 429 with
 * Retry-After and does not count the refused request. A GET is repeated after
 * the wait; a write never is, because repeating it could file or pay twice.
 */
async function sendWithRateLimitRecovery(path: string, init: RequestInit): Promise<Response> {
  const isRead = (init.method ?? 'GET').toUpperCase() === 'GET'
  if (!isRead) writesSent += 1
  let bearerRetried = false
  for (let retry = 0; ; retry += 1) {
    const response = await send(path, await authorizedInit(init, { freshToken: bearerRetried }))
    // The Worker refused the bearer it was sent (expired, or signed by a key
    // it no longer serves). Every customer route verifies the JWT before its
    // handler runs, so nothing happened and a write is as safe to repeat as a
    // read. Mint a fresh token and try once more; a second 401 is the answer
    // (NONRESIDENTTAX-FE). A streamed body cannot be sent twice.
    if (response.status === 401 && !bearerRetried && isRepeatableBody(init.body)) {
      bearerRetried = true
      continue
    }
    if (response.status !== 429 || !isRead || retry >= RATE_LIMIT_RETRIES) return response
    await new Promise((resolve) => setTimeout(resolve, rateLimitWaitMs(response)))
  }
}

function isRepeatableBody(body: BodyInit | null | undefined): boolean {
  return !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
}

/** Calls a ProductData customer route and returns its payload. */
export async function productData<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await sendWithRateLimitRecovery(path, init)
  const text = await response.text()

  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = null
    }
  }

  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>
    if (obj.success === false) {
      // A plain string `error` (R34) carries its own message verbatim; the
      // `{code,message}` object shape keeps its existing fallbacks.
      if (typeof obj.error === 'string') {
        throw new ProductDataError(response.status, 'HTTP_ERROR', obj.error)
      }
      const failure = obj as {
        error?: { code?: string; message?: string; violations?: RuleViolation[]; orderId?: unknown }
      }
      const violations = Array.isArray(failure.error?.violations) ? failure.error.violations : undefined
      const orderId = typeof failure.error?.orderId === 'string' ? failure.error.orderId : undefined
      throw new ProductDataError(
        response.status,
        failure.error?.code ?? 'HTTP_ERROR',
        failure.error?.message ?? `HTTP ${response.status}`,
        violations || orderId ? { violations, orderId } : undefined,
      )
    }
    // `data` key presence, not truthiness, is the envelope signal — `data:
    // null` is still a real envelope. A `success: true` body with no `data`
    // key (e.g. `{ success: true, address: {...} }`) is NOT an envelope; it
    // falls through to the untouched pass-through below.
    if (obj.success === true && 'data' in obj) {
      return (obj as { data: T }).data
    }
  }

  if (!response.ok) {
    const legacy = body as { error?: unknown } | null
    const message = typeof legacy?.error === 'string' ? legacy.error : `HTTP ${response.status}`
    throw new ProductDataError(response.status, 'HTTP_ERROR', message)
  }

  return body as T
}

/**
 * The same authorized call, returning the raw `Response`.
 * For binary or streamed endpoints (a PDF, a signed-URL redirect) where the
 * envelope unwrap above would be wrong.
 */
export async function productDataRaw(path: string, init: RequestInit = {}): Promise<Response> {
  return sendWithRateLimitRecovery(path, init)
}
